/**
 * CrisisCallout — the rose-tinted "you're not alone right now" card.
 *
 * Lives at the bottom of any user-facing surface where distress signals
 * could plausibly land: Community feed (parents posting about hard nights),
 * Help center (where someone might search for "I need help"), and the
 * Bills Resources panel (financial stress is real).
 *
 * Composition mirrors the editorial card recipe — rose left rail, mono
 * eyebrow, italic-serif headline, two crisis contacts (Victor, 2026-10-14:
 * 911 if the danger is immediate, otherwise 988; no separate text line):
 *   - 988 Suicide & Crisis Lifeline, with the route for the reader's language
 *     (es: press 2 or text AYUDA; zh, pt, fr: call and ask for an interpreter)
 *   - 911, for danger happening right now
 *
 * No TRF case manager row since 2026-10-11 (#2566): TRF is not a crisis
 * line and its staff are not trained for crisis support, so the card must
 * not offer them as a crisis contact.
 *
 * SAFETY RULE (from the editorial-redesign skill, non-negotiable):
 * AI must NEVER appear as a crisis resource here. No Ember mention. No
 * "ask the companion". The two contacts above are the only options.
 */
import { Phone, Siren } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { MONO_LABEL_TYPE } from '@/components/editorial';
import { CRISIS_PARAMS } from '@/features/chat-trf/crisis/resources';

interface CrisisCalloutProps {
  /**
   * Optional override for the persona-aware eyebrow (e.g. "Help center · crisis").
   * Rendered as-authored: the label face, size and colour carry the eyebrow
   * register, so no dash is prefixed (design grammar v3.3).
   */
  eyebrow?: string;
}

export function CrisisCallout({ eyebrow }: CrisisCalloutProps) {
  // #2109 R3-B8: the words follow the reader's language; the numbers are
  // params from CRISIS_RESOURCES and are the same in every language.
  const { t } = useTranslation('crisis');
  const detail = <span className="text-status-danger-fg" />;
  return (
    <aside
      role="complementary"
      aria-label={t('callout.ariaLabel')}
      className="relative rounded-lg border border-status-danger-border bg-status-danger/40 overflow-hidden"
    >
      <span className="absolute left-0 top-0 h-full w-[3px] bg-status-danger-fg" aria-hidden />

      <header className="px-5 pt-4 pb-2">
        <p className={cn(MONO_LABEL_TYPE, 'text-status-danger-fg mb-1')}>
          {eyebrow ?? t('callout.eyebrow')}
        </p>
        <h3 className="font-serif text-lg leading-tight text-status-danger-fg italic">
          {t('callout.headline')}
        </h3>
      </header>

      <ul className="px-5 pb-4 space-y-2 text-body">
        <li className="flex items-start gap-2.5">
          <Phone className="h-3.5 w-3.5 mt-1 text-status-danger-fg flex-shrink-0" />
          <span>
            <Trans
              t={t}
              i18nKey="callout.lifeline"
              values={CRISIS_PARAMS}
              components={{
                call: <a href={`tel:${CRISIS_PARAMS.lifeline}`} className="font-medium text-status-danger-fg underline-offset-2 hover:underline" />,
                detail,
              }}
            />
          </span>
        </li>
        <li className="flex items-start gap-2.5">
          <Siren className="h-3.5 w-3.5 mt-1 text-status-danger-fg flex-shrink-0" />
          <span>
            <Trans
              t={t}
              i18nKey="callout.emergency"
              values={CRISIS_PARAMS}
              components={{
                call: <a href={`tel:${CRISIS_PARAMS.emergency}`} className="font-medium text-status-danger-fg underline-offset-2 hover:underline" />,
                detail,
              }}
            />
          </span>
        </li>
      </ul>
    </aside>
  );
}
